import type { FunctionComponent } from 'react';

import { Link } from 'react-router';

import { useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import { useMenuListPath } from 'components/MenuManagement/hooks/useMenuListPath';

import { Box, Button, Divider, Flex, Group } from '@mantine/core';

import MenuPublishButton from '../MenuActions/MenuPublishButton';
import MenuEnabledSwitch from '../MenuActions/MenuEnabledSwitch';

type MenuFormButtonsProps = {
  checkMenuTouched?: () => boolean;
  formSubmitted?: boolean;
  handleSave?: () => void;
  isEdit?: boolean;
  menu?: Record<string, unknown>;
  resetFields: () => void;
  setMenuEnabled?: (enabled: boolean) => void;
};

export const MenuFormButtons: FunctionComponent<MenuFormButtonsProps> = ({
  checkMenuTouched,
  handleSave,
  isEdit,
  resetFields,
  formSubmitted,
  menu,
  setMenuEnabled,
}) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });
  const menuListPath = useMenuListPath();

  const showSwitch = isEdit && !isMobile;

  return (
    <Box
      bottom="0"
      pos="fixed"
      bg="var(--surface-secondary)"
      w={{ lg: 'calc(100% - 200px)' }}
      left={{ base: 0, lg: 'auto' }}
      right={{ base: 0, lg: 'auto' }}
      ml={{ lg: 'calc(var(--mantine-spacing-xl) * -1)' }}
      style={{ zIndex: 'calc(var(--mantine-z-index-modal) - 1)' }}
    >
      <Divider />
      <Flex
        justify="space-between"
        align="center"
        gap={isMobile ? 'sm' : undefined}
        direction={isMobile ? 'column-reverse' : 'row'}
        px={{ sm: 'sm', md: 'xl' }}
        py="sm"
      >
        <Group gap="md">
          {showSwitch && <MenuEnabledSwitch menu={menu} setMenuEnabled={setMenuEnabled} />}
          <Button onClick={resetFields} variant="link" fz="md" lh="var(--dimension-md)">
            Reset
          </Button>
          <Button
            to={menuListPath}
            variant="link"
            component={Link}
            fz="md"
            lh="var(--dimension-md)"
          >
            Cancel
          </Button>
        </Group>
        <Group gap="xs" w={isMobile ? '100%' : undefined}>
          <Button
            variant={isEdit ? 'outline' : 'filled'}
            size="md"
            loading={formSubmitted}
            type="submit"
            w={140}
            style={{ flexGrow: 1 }}
          >
            Save
          </Button>
          {isEdit && (
            <MenuPublishButton
              checkMenuTouched={checkMenuTouched}
              handleSave={handleSave}
              menu={menu}
            />
          )}
        </Group>
      </Flex>
    </Box>
  );
};
